import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ApplyLeaveDto {
  date: string;
  isHalfDay: boolean;
  reason: string;
  isUnplanned?: boolean;
}

export interface ApplyMultipleLeavesDto {
  dates: string[];
  isHalfDay: boolean;
  reason: string;
  isUnplanned?: boolean;
}

export interface ApproveRejectLeaveDto {
  comment?: string;
}

export interface AgentLeaveDto {
  id?: string;
  agentId: string;
  agentName?: string;
  date: string;
  isHalfDay: boolean;
  reason: string;
  status: number; // 0 = Pending, 1 = Approved, 2 = Rejected, 3 = Unplanned
  adminComment?: string;
  deductedQuotaDays: number;
  createdAt?: string;
}

export interface AgentLeaveQuotaDto {
  agentId: string;
  agentName: string;
  availableLeavesBalance: number;
  allowedLeavesPerMonth: number;
  informedLeavePenaltyAmount: number;
  uninformedLeavePenaltyAmount: number;
}

export interface UpdateAgentLeaveConfigDto {
  allowedLeavesPerMonth?: number;
  informedLeavePenaltyAmount?: number;
  uninformedLeavePenaltyAmount?: number;
  availableLeavesBalance?: number;
}

export interface AgentAttendanceDayDto {
  date: string;
  status: string; // "Present", "Holiday", "PlannedLeave", "UnplannedLeave"
  description?: string;
  isStandard: boolean;
  isHalfDay: boolean;
  presentWeight: number;
  plannedLeaveWeight: number;
  unplannedLeaveWeight: number;
}

export interface PaginatedAgentLeaveResponse {
  items: AgentLeaveDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

@Injectable({
  providedIn: 'root'
})
export class AgentLeaveService {
  private readonly baseUrl = `${environment.apiConfig.uri}/api/AgentLeave`;

  constructor(private http: HttpClient) { }

  applyLeave(dto: ApplyLeaveDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/apply`, dto);
  }

  applyLeaveForAgent(agentId: string, dto: ApplyLeaveDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/agent/${agentId}/apply`, dto);
  }

  applyMultipleLeaves(agentId: string, dto: ApplyMultipleLeavesDto): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/agent/${agentId}/apply-multiple`, dto);
  }

  approveLeave(leaveId: string, agentId: string, comment?: string): Observable<any> {
    const body: ApproveRejectLeaveDto = { comment };
    return this.http.put<any>(`${this.baseUrl}/${leaveId}/agent/${agentId}/approve`, body);
  }

  rejectLeave(leaveId: string, agentId: string, comment: string): Observable<any> {
    const body: ApproveRejectLeaveDto = { comment };
    return this.http.put<any>(`${this.baseUrl}/${leaveId}/agent/${agentId}/reject`, body);
  }

  getAgentLeaves(agentId: string): Observable<AgentLeaveDto[]> {
    return this.http.get<AgentLeaveDto[]>(`${this.baseUrl}/agent/${agentId}`);
  }

  getLeavesForApproval(
    pageIndex: number = 0,
    pageSize: number = 10,
    status?: number,
    agentId?: string,
    startDate?: string,
    endDate?: string
  ): Observable<PaginatedAgentLeaveResponse> {
    const params: any = {
      pageIndex: pageIndex.toString(),
      pageSize: pageSize.toString()
    };
    if (status !== undefined && status !== null) {
      params.status = status.toString();
    }
    if (agentId) {
      params.agentId = agentId;
    }
    if (startDate) {
      params.startDate = startDate;
    }
    if (endDate) {
      params.endDate = endDate;
    }

    const queryString = Object.keys(params)
      .map(key => `${key}=${encodeURIComponent(params[key])}`)
      .join('&');

    return this.http.get<PaginatedAgentLeaveResponse>(`${this.baseUrl}?${queryString}`);
  }

  getAgentQuota(agentId: string): Observable<AgentLeaveQuotaDto> {
    return this.http.get<AgentLeaveQuotaDto>(`${this.baseUrl}/quota/agent/${agentId}`);
  }

  updateAgentConfig(agentId: string, dto: UpdateAgentLeaveConfigDto): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/config/agent/${agentId}`, dto);
  }

  getAgentAttendance(agentId: string, year: number, month: number): Observable<AgentAttendanceDayDto[]> {
    return this.http.get<AgentAttendanceDayDto[]>(`${this.baseUrl}/attendance/agent/${agentId}?year=${year}&month=${month}`);
  }
}
